import { reducedStream, reducer, streamReducer } from '@ardoq/rxbeach';
import { defer, distinctUntilChanged, map, of } from 'rxjs';
import { Features, PersonalSetting } from '@ardoq/api-types';
import { currentUserInterface } from 'modelInterface/currentUser/currentUserInterface';
import { setStateProperty, updateState } from '@ardoq/common-helpers';
import { featureContext$ } from 'streams/featureContext$';
import { featureOperations } from '@ardoq/features';
import {
  openChatWindow,
  setAssistantDisplayMode,
  setPanelWidth,
  closeChatWindow,
  minimizeChatWindow,
  hideDockedChatWindow,
  initializeNewChat,
  sendMessageWithHistory,
  openFullscreenChatAndSend,
  openChatAndPopulate,
  sendMessageFailed,
  messageCancellationRequested,
  messageCancellationFailed,
  saveSubmittedFeedback,
  setAutoScrollInLastMessage,
  setCurrentScrollTop,
  chatTimedOut,
  dismissSendMessageError,
  showChatHistory,
  hideChatHistory,
  openSnakeGame,
  closeSnakeGame,
  setShowScrollDownButton,
  setActiveChatSessionId,
  setCurrentActiveRequest,
  setInput,
  setEditingPlanMessageId,
  appendInput,
  setAssistantConfig,
  historicChatLoaded,
  setDisplayModifier,
  triggerDisplayModeHint,
  dismissDisplayModeHint,
  setActiveOnboardingHintKey,
  genericChatErrorReceived,
  appendUserFriendlyChatError,
  requestAssistantInputFocus,
  addBanner,
  dismissBanner,
  agentRunRestored,
  agentRunResumeFailed,
  lastOpenedSessionRestoreStarted,
  lastOpenedSessionRestoreFinished,
} from './actions';
import {
  interactionAnswered,
  interactionRequested,
  interactionResolved,
  interactionSubmissionFailed,
} from './actions';
import type { AgentRunRestoration } from './actions';
import { showAppModule } from 'appContainer/actions';
import { viewpointModeEntered$ } from 'traversals/loadedGraph$';
import {
  assistantStateOperations,
  AssistantMode,
  AssistantScreen,
  getInitialAssistantState,
  type AssistantState,
} from './assistantStateOperations';
import type { DecoratedSendMessageRequest } from './types';
import {
  websocketChatMessageToolCall$,
  websocketChatMessageDone$,
  websocketChatInteractionRequested$,
} from './websocketStreams$';

export type { AssistantState } from './assistantStateOperations';
export {
  defaultAssistantState,
  AssistantMode,
  AssistantScreen,
} from './assistantStateOperations';

const handleOpenFullscreenChatAndSend = (
  state: AssistantState,
  payload: DecoratedSendMessageRequest
): AssistantState =>
  assistantStateOperations.handleSendMessage(
    assistantStateOperations.handleInitializeNewChat(
      assistantStateOperations.handleSetDisplayMode(state, 'fullScreen')
    ),
    payload
  );

export const handleOpenChatAndPopulate = (
  state: AssistantState,
  { content }: { content: string }
): AssistantState => ({
  ...assistantStateOperations.handleOpenChatWindow(state),
  screen: AssistantScreen.Chat,
  input: content,
  hasPendingInputFocusRequest: true,
  isRestoringLastOpenedSession: false,
});

const handleAgentRunRestored = (
  state: AssistantState,
  { exchangeId, toolCalls }: AgentRunRestoration
): AssistantState =>
  toolCalls.reduce(assistantStateOperations.applyToolCall, {
    ...state,
    currentActiveRequest: exchangeId,
  });

export const assistantState$ = reducedStream(
  'assistantState',
  defer(() =>
    of(
      getInitialAssistantState(
        currentUserInterface.getPersonalSetting(
          PersonalSetting.ASSISTANT_PREFERENCES
        )
      )
    )
  ),
  [
    // Window management
    reducer(openChatWindow, assistantStateOperations.handleOpenChatWindow),
    reducer(
      setAssistantDisplayMode,
      assistantStateOperations.handleSetDisplayMode
    ),
    reducer(setDisplayModifier, setStateProperty('displayModifier')),
    reducer(
      triggerDisplayModeHint,
      updateState({ animateDisplayModeHint: true })
    ),
    reducer(
      dismissDisplayModeHint,
      updateState({ animateDisplayModeHint: false })
    ),
    reducer(
      setActiveOnboardingHintKey,
      setStateProperty('activeOnboardingHintKey')
    ),
    reducer(setPanelWidth, setStateProperty('panelWidth')),
    reducer(setAssistantConfig, setStateProperty('assistantConfig')),
    reducer(closeChatWindow, assistantStateOperations.handleCloseChatWindow),
    reducer(
      hideDockedChatWindow,
      assistantStateOperations.handleHideDockedChatWindow
    ),
    reducer(
      minimizeChatWindow,
      updateState({
        mode: AssistantMode.Minimized,
        screen: AssistantScreen.Chat,
        animateDisplayModeHint: false,
        activeOnboardingHintKey: null,
        isSnakeGameOpen: false,
      })
    ),

    // Chat initialization
    reducer(
      initializeNewChat,
      assistantStateOperations.handleInitializeNewChat
    ),
    // Send message
    reducer(sendMessageWithHistory, assistantStateOperations.handleSendMessage),
    reducer(openFullscreenChatAndSend, handleOpenFullscreenChatAndSend),
    reducer(openChatAndPopulate, handleOpenChatAndPopulate),
    reducer(
      sendMessageFailed,
      assistantStateOperations.handleSendMessageFailed
    ),

    // Cancellation
    reducer(messageCancellationRequested, updateState({ cancelling: true })),
    reducer(messageCancellationFailed, updateState({ cancelling: false })),

    // WebSocket events
    streamReducer(
      websocketChatMessageToolCall$,
      assistantStateOperations.setActiveToolDescription
    ),
    streamReducer(
      websocketChatMessageDone$,
      assistantStateOperations.setIsCurrentChatMessageDone
    ),
    reducer(
      genericChatErrorReceived,
      assistantStateOperations.setSendMessageError
    ),
    reducer(
      appendUserFriendlyChatError,
      updateState({
        currentActiveRequest: null,
        handledInteractionId: null,
        activeInteractions: {},
      })
    ),

    // Feedback
    reducer(
      saveSubmittedFeedback,
      assistantStateOperations.saveSubmittedFeedbackReducer
    ),

    // Scroll & UI
    reducer(
      setAutoScrollInLastMessage,
      setStateProperty('autoScrollInLastMessage')
    ),
    reducer(setCurrentScrollTop, setStateProperty('currentScrollTop')),
    reducer(setShowScrollDownButton, setStateProperty('showScrollDownButton')),
    reducer(
      chatTimedOut,
      updateState({
        currentActiveRequest: null,
        cancelling: false,
        hasChatTimedOut: true,
        handledInteractionId: null,
        activeInteractions: {},
      })
    ),
    reducer(dismissSendMessageError, updateState({ sendMessageError: null })),

    // History screen navigation
    reducer(showChatHistory, updateState({ screen: AssistantScreen.History })),
    reducer(hideChatHistory, updateState({ screen: AssistantScreen.Chat })),

    // Snake minigame
    reducer(openSnakeGame, updateState({ isSnakeGameOpen: true })),
    reducer(closeSnakeGame, updateState({ isSnakeGameOpen: false })),
    reducer(
      historicChatLoaded,
      assistantStateOperations.handleHistoricChatLoaded
    ),
    reducer(setActiveChatSessionId, setStateProperty('chatSessionId')),
    reducer(setCurrentActiveRequest, setStateProperty('currentActiveRequest')),
    reducer(agentRunRestored, handleAgentRunRestored),
    reducer(
      agentRunResumeFailed,
      assistantStateOperations.handleAgentRunWatchRestoreFailed
    ),
    reducer(
      lastOpenedSessionRestoreStarted,
      updateState({ isRestoringLastOpenedSession: true })
    ),
    reducer(
      lastOpenedSessionRestoreFinished,
      updateState({ isRestoringLastOpenedSession: false })
    ),

    reducer(showAppModule, assistantStateOperations.exitFullScreenOnNavigation),
    streamReducer(
      viewpointModeEntered$,
      assistantStateOperations.exitFullScreenOnNavigation
    ),

    // Input (absorbed from inputState$)
    reducer(setInput, setStateProperty('input')),
    reducer(setEditingPlanMessageId, setStateProperty('editingPlanMessageId')),
    reducer(appendInput, assistantStateOperations.handleAppendInput),

    // Banners
    reducer(addBanner, assistantStateOperations.handleAddBanner),
    reducer(dismissBanner, assistantStateOperations.handleDismissBanner),

    // Durable interaction lifecycle
    streamReducer(
      websocketChatInteractionRequested$,
      assistantStateOperations.trackInteractionRequested
    ),
    reducer(
      interactionRequested,
      assistantStateOperations.setHandledInteractionId
    ),
    reducer(
      interactionResolved,
      assistantStateOperations.handleInteractionResolved
    ),
    reducer(
      interactionSubmissionFailed,
      assistantStateOperations.handleInteractionSubmissionFailed
    ),
    reducer(
      interactionAnswered,
      assistantStateOperations.handleInteractionSubmitting
    ),

    // Input focus
    reducer(
      requestAssistantInputFocus,
      setStateProperty('hasPendingInputFocusRequest')
    ),

    // Feature flags
    streamReducer(
      featureContext$.pipe(
        map(featureContext =>
          featureOperations.hasFeature(
            featureContext,
            Features.AI_TMP_PYTHON_AGENTS
          )
        )
      ),
      setStateProperty('hasPythonAgentsFeature')
    ),
    streamReducer(
      featureContext$.pipe(
        map(featureContext =>
          featureOperations.hasFeature(
            featureContext,
            Features.AI_TMP_INSIGHT_AGENT
          )
        )
      ),
      setStateProperty('hasInsightAgentFeature')
    ),
    streamReducer(
      featureContext$.pipe(
        map(featureContext =>
          featureOperations.hasFeature(
            featureContext,
            Features.AI_TMP_CUSTOM_AGENT_EDITING
          )
        )
      ),
      setStateProperty('hasCustomAgentsManagementAccess')
    ),
  ]
);

export const assistantMode$ = assistantState$.pipe(
  map(state => state.mode),
  distinctUntilChanged()
);
